export type DevicePixelRatioWatchCallback = (devicePixelRatio: number) => void;

interface DevicePixelRatioState {
	current: number;
	mediaQuery: MediaQueryList;
	watchers: DevicePixelRatioWatchCallback[];
}

let state: DevicePixelRatioState | null = null;

/**
 * Creates the lazy browser subscription used to detect device-pixel-ratio changes.
 * Server-side and reduced browser environments are intentionally left uninitialized
 * so a later browser call can still establish the real media-query listener.
 *
 * @returns Shared {@link DevicePixelRatioState}, or `null` when browser APIs are unavailable.
 */
function ensureInitialized(): DevicePixelRatioState | null {
	if (state) {
		return state;
	}

	if (
		typeof window === 'undefined'
		|| typeof window.matchMedia !== 'function'
	) {
		return null;
	}

	const initialDevicePixelRatio = getWindowDevicePixelRatio();
	const initialState: DevicePixelRatioState = {
		current: initialDevicePixelRatio,
		mediaQuery: window.matchMedia(`(resolution: ${initialDevicePixelRatio}dppx)`),
		watchers: [],
	};

	/**
	 * Rebinds the one-shot resolution query and notifies every active subscriber.
	 */
	function handleChange(): void {
		const nextDevicePixelRatio = getWindowDevicePixelRatio();

		initialState.current = nextDevicePixelRatio;
		initialState.mediaQuery.removeEventListener(
			'change',
			handleChange,
		);
		initialState.mediaQuery = window.matchMedia(`(resolution: ${nextDevicePixelRatio}dppx)`);
		initialState.mediaQuery.addEventListener(
			'change',
			handleChange,
		);

		for (const watcher of [...initialState.watchers]) {
			invokeSafely(
				watcher,
				nextDevicePixelRatio,
			);
		}
	}

	initialState.mediaQuery.addEventListener(
		'change',
		handleChange,
	);
	state = initialState;

	return initialState;
}

/**
 * Reads a finite positive ratio from the current browser window.
 *
 * @returns Browser device-pixel ratio, falling back to `1` when unavailable or invalid.
 */
function getWindowDevicePixelRatio(): number {
	if (typeof window === 'undefined') {
		return 1;
	}

	const devicePixelRatio = window.devicePixelRatio;

	return (
		Number.isFinite(devicePixelRatio)
		&& devicePixelRatio > 0
			? devicePixelRatio
			: 1
	);
}

/**
 * Invokes one ratio subscriber without allowing application callback failures to
 * prevent the remaining subscribers from observing the browser change.
 *
 * @param callback {@link DevicePixelRatioWatchCallback} registered by a consumer.
 * @param devicePixelRatio New browser ratio delivered to the callback.
 */
function invokeSafely(
	callback: DevicePixelRatioWatchCallback,
	devicePixelRatio: number,
): void {
	try {
		callback(devicePixelRatio);
	} catch {
		/** Consumer callback failures do not belong to this browser observer. */
	}
}

/**
 * Returns the ratio tracked by the lazy resolution media-query observer.
 *
 * @returns Current browser device-pixel ratio, or `1` outside a supported browser.
 */
export function getDevicePixelRatio(): number {
	const currentDevicePixelRatio = getWindowDevicePixelRatio();
	const watcherState = ensureInitialized();

	if (watcherState) {
		watcherState.current = currentDevicePixelRatio;
	}

	return currentDevicePixelRatio;
}

/**
 * Subscribes to browser device-pixel-ratio changes without creating any eager
 * DOM observers at module evaluation time.
 *
 * @param callback {@link DevicePixelRatioWatchCallback} notified after a ratio change.
 * @returns Cleanup callback that removes the supplied watcher.
 */
export function watchDevicePixelRatio(callback: DevicePixelRatioWatchCallback): () => void {
	const watcherState = ensureInitialized();

	if (!watcherState) {
		return (): void => {};
	}

	watcherState.watchers.push(callback);

	return (): void => {
		const watcherIndex = watcherState.watchers.indexOf(callback);

		if (watcherIndex >= 0) {
			watcherState.watchers.splice(
				watcherIndex,
				1,
			);
		}
	};
}
